import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAuthHeader, handleUnauthorized } from '../api/core'
import { useStore } from '../store'
import type { DocType, TdLine, PriceListSummary, Template, FormState } from '../types/testdata.types'
import { useErrorInjection } from './useErrorInjection'
import {
  LOCALES, COUNTRY_OPTIONS, countryForLocale, countrySupportsLocale, documentLabelsForLocale, localeOptionsForCountry, vatRatesForLocale,
} from '../config/locales'
import type { CountryCode } from '../config/locales'
import { localeSupportsRecognitionText } from '../config/localeRecognition'
import {
  DOC_LABELS, VAT_CODE_OPTIONS,
  LS_COUNTRY, LS_LOCALE, LS_XML_FORMAT, LS_XML_PROFILE, LS_FACTURX_PROFILE,
  emptyForm, emptyLine, buildDocNumber, dematerializeFinancialScenarioLine,
} from './testdata-form/constants'
import type { XmlFormat, XmlProfile, FacturxProfile, FinancialScenarioId } from './testdata-form/constants'
export { FINANCIAL_SCENARIO_OPTIONS } from './testdata-form/constants'
export type { FinancialScenarioId }
import { useLogoState } from './testdata-form/useLogoState'
import { useVatOverrides } from './testdata-form/useVatOverrides'
import { useLinesState } from './testdata-form/useLinesState'
import { useFinancialScenarioPreview } from './testdata-form/useFinancialScenarioPreview'
import { useTemplates, applyTemplateToForm } from './testdata-form/useTemplates'
import { usePartyPresets } from './testdata-form/usePartyPresets'
import { usePriceListsAndRandomLines } from './testdata-form/usePriceListsAndRandomLines'
import { useDocNumber } from './testdata-form/useDocNumber'
import { documentDisplayLabels } from './testdata-form/documentDisplayLabel'
import { freezeSnapshot } from './testdata-form/sendConfirmation'
import type { EmailRequestSnapshot } from './testdata-form/sendConfirmation'

export type { DocType, TdLine, PriceListSummary, Template, FormState }
export { COUNTRY_OPTIONS }
export type { CountryCode }
export { DOC_LABELS, VAT_CODE_OPTIONS, emptyLine, buildDocNumber }
export type { XmlFormat, XmlProfile, FacturxProfile }

export function useTestDataForm() {
  const { t } = useTranslation()
  const { currentUser } = useStore()
  const storedCountry = (localStorage.getItem(LS_COUNTRY) as CountryCode) ?? 'NL'
  const [country, setCountry] = useState<CountryCode>(storedCountry)
  const localeConfig = LOCALES[country]
  const [activeLocale, setActiveLocale] = useState<string>(() => {
    const storedLocale = localStorage.getItem(LS_LOCALE)
    return storedLocale && countrySupportsLocale(storedCountry, storedLocale)
      ? storedLocale
      : LOCALES[storedCountry].locale
  })
  const localeOptions = localeOptionsForCountry(country)
  const localeDocLabels = documentLabelsForLocale(country, activeLocale)
  const uiDocLabels: Record<DocType, string> = {
    invoice: t('testdata_email_modal.doc_invoice'),
    order_response: t('testdata_email_modal.doc_order_response'),
    despatch_advice: t('testdata_email_modal.doc_despatch_advice'),
    credit_note: t('testdata_email_modal.doc_credit_note'),
  }
  const docLabels = documentDisplayLabels(uiDocLabels, localeDocLabels)
  const vatCodeOptions = vatRatesForLocale(country, activeLocale).map(r => ({ value: r.code, label: r.label }))

  const [docType, setDocTypeRaw] = useState<DocType>('invoice')
  const [xmlFormat, setXmlFormat] = useState<XmlFormat>(
    () => (localStorage.getItem(LS_XML_FORMAT) as XmlFormat) ?? 'sales005'
  )
  const [xmlProfile, setXmlProfileRaw] = useState<XmlProfile>(
    () => (localStorage.getItem(LS_XML_PROFILE) as XmlProfile) ?? 'none'
  )
  const [facturxProfile, setFacturxProfileRaw] = useState<FacturxProfile>(
    () => (localStorage.getItem(LS_FACTURX_PROFILE) as FacturxProfile) ?? 'en16931'
  )

  const handleXmlFormatChange = (fmt: XmlFormat) => {
    setXmlFormat(fmt)
    localStorage.setItem(LS_XML_FORMAT, fmt)
    // REQ-TESTDATA-007: leaving the only supported format/doctype combination
    // clears the scenario the same way "switching back to Normal" does —
    // the (possibly materialized) visible lines stay, just unlocked.
    if (fmt !== 'sales005' && financialScenarioId) clearFinancialScenario()
  }

  const setXmlProfile = (profile: XmlProfile) => {
    setXmlProfileRaw(profile)
    localStorage.setItem(LS_XML_PROFILE, profile)
  }

  const setFacturxProfile = (profile: FacturxProfile) => {
    setFacturxProfileRaw(profile)
    localStorage.setItem(LS_FACTURX_PROFILE, profile)
  }

  const setDocType = (dt: DocType) => {
    setDocTypeRaw(dt)
    if (dt === 'order_response' || dt === 'despatch_advice') {
      setXmlFormat(prev => (prev === 'cii' || prev === 'facturx') ? 'sales005' : prev)
    }
    // REQ-TESTDATA-005 (CAP-003 v1.0) — a financial scenario is only
    // available for document_type="invoice" + xml_format="sales005" in this
    // release (see schemas.TestdataRequest._financial_scenario_requires_invoice_sales005
    // on the backend). Switching away from that combination clears the
    // selection instead of silently submitting an unsupported request.
    if (dt !== 'invoice' && financialScenarioId) clearFinancialScenario()
  }

  // REQ-TESTDATA-005 (CAP-003 v1.0) — "Financial scenario" selector (§18).
  // `undefined` = "Normal/current" (default, existing generation unchanged).
  const [financialScenarioId, setFinancialScenarioIdRaw] = useState<FinancialScenarioId | undefined>(undefined)
  const financialScenarioSupported = docType === 'invoice' && xmlFormat === 'sales005'
  // DK has no "reduced" VAT category — FIN-MULTIVAT-001 is a genuine
  // capability mismatch the backend 422s on — disable it client-side too so
  // the user gets an explanation instead of a failed submit.
  const financialScenarioUnsupportedForCountry =
    country === 'DK' && financialScenarioId === 'FIN-MULTIVAT-001'
  const financialScenarioActive =
    !!financialScenarioId && financialScenarioSupported && !financialScenarioUnsupportedForCountry
  // DEC-FIN-001 re-enabled FIN-DOC-DISCOUNT-001/FIN-COMBINED-001 — the
  // document allowance targets the fixed 'standard' VAT category, resolved
  // server-side (`financial_scenarios.validate_scenario_capabilities`). Every
  // currently-supported country/date profile exposes 'standard', so no
  // client-side gate exists for it (unlike the genuine DK 'reduced' gap
  // above); the backend still 422s with a clear DEC-FIN-001 reason for a
  // future profile that somehow lacks 'standard', surfaced through the
  // normal submission-error path.

  const [form, setForm] = useState<FormState>(() => emptyForm(storedCountry))
  const [saveAsTemplate, setSaveAsTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)
  const [showEmailModal, setShowEmailModal] = useState(false)
  // REQ-TESTDATA-011: the one request snapshot the confirmation modal both
  // summarizes and sends (what-you-review == what-you-send).
  const [emailSnapshot, setEmailSnapshot] = useState<EmailRequestSnapshot | null>(null)

  const linesState = useLinesState(country)
  const { lines, setLines, roundUnitPrices, roundUnitPriceValue } = linesState

  // REQ-TESTDATA-007 (CAP-003 v1.2) — Financial Scenario Preview & line-editor
  // materialization. `scenarioPreview.resolve(...)` calls the read-only
  // `/api/testdata/financial-scenario-preview` endpoint and replaces the
  // visible `lines` with the backend-resolved canonical materialization —
  // never a frontend-computed value (see hook docstring for the source-line
  // snapshot/idempotency contract).
  const scenarioPreview = useFinancialScenarioPreview()

  const refreshFinancialScenarioPreview = (scenarioId: FinancialScenarioId) => {
    void scenarioPreview.resolve({
      scenarioId, locale: activeLocale, documentDate: form.document_date,
      currentLines: linesState.lines, lineGeneration: linesState.lineGeneration,
      setLines: linesState.setLines,
    })
  }

  const selectFinancialScenario = (id: FinancialScenarioId | undefined) => {
    if (id === undefined) {
      clearFinancialScenario()
      return
    }
    // Actual resolution happens in the effect below (keyed on
    // `financialScenarioId`) — this just records the selection so a single
    // effect run is the one place that calls the preview endpoint, instead
    // of double-resolving on first selection.
    setFinancialScenarioIdRaw(id)
  }

  // REQ-TESTDATA-007 explicit product decision: Scenario -> Normal clears
  // financial_scenario_id and hides the preview. Visible values remain
  // unchanged; scenario-only materialization metadata is cleared
  // (`dematerializeFinancialScenarioLine` — see its docstring), and the
  // rows become true ordinary editable lines. No previous ordinary-line set
  // is ever restored (`scenarioPreview.clear()` never touches `lines`; this
  // transforms whatever is CURRENTLY VISIBLE, it does not replace it with
  // anything else).
  //
  // Review fix: an earlier revision left every scenario-only canonical
  // field (net_amount_override, scenario_identity_base, etc.) on the
  // now-"ordinary" rows. That looked editable but wasn't: the backend still
  // preferred net_amount_override over quantity x unit_price, so an edit
  // after switching to Normal could silently be ignored at Generate time,
  // and stale scenario_identity_base could leak into a later scenario's
  // identity resolution instead of the user's edited article_number.
  const clearFinancialScenario = () => {
    setFinancialScenarioIdRaw(undefined)
    scenarioPreview.clear()
    linesState.setLines(ls => ls.map(dematerializeFinancialScenarioLine))
  }

  // Re-resolve the active scenario whenever an input its resolution actually
  // depends on changes (locale/country, invoice date, or the Precision Test
  // config) — REQ-TESTDATA-007 "Precision + Financial Scenario": a config
  // change while a scenario is active MUST refresh, never leave a stale
  // preview/materialization silently in place.
  useEffect(() => {
    if (!financialScenarioId || !financialScenarioSupported || financialScenarioUnsupportedForCountry) return
    refreshFinancialScenarioPreview(financialScenarioId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financialScenarioId, activeLocale, form.document_date, linesState.lineGeneration])

  const logo = useLogoState(currentUser?.id)
  const vat = useVatOverrides()
  const templatesState = useTemplates()
  const partyPresets = usePartyPresets(currentUser?.id)
  const restoredPartyUserIdRef = useRef<number | null>(null)
  const priceLists = usePriceListsAndRandomLines()
  // REQ-TESTDATA-004 (v0.8.1): changing the source price list while Precision
  // test is on invalidates the current preview exactly like a settings change
  // (§5 "select a different price list and regenerate").
  const setRandomPriceListId = (id: number) => {
    priceLists.setRandomPriceListId(id)
    if (linesState.lineGeneration.mode === 'precision_test') linesState.setPrecisionMaterialized(false)
  }
  const isPrecisionStale = linesState.lineGeneration.mode === 'precision_test' && !linesState.precisionMaterialized
  const {
    docNumCounterRef, refreshDocCounter, documentNumberMode, markDocumentNumberManual,
    ensureCurrentAutoDocumentNumber,
  } = useDocNumber(docType, currentUser?.initials ?? '', setForm)

  useEffect(() => {
    templatesState.loadTemplates()
    partyPresets.loadPresets()
    priceLists.loadPriceLists()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templatesState.loadTemplates, partyPresets.loadPresets, priceLists.loadPriceLists])

  // REQ-TESTDATA-009 — once the authoritative preset list and the current
  // user's remembered ids are available, restore through the exact same
  // application path as a manual dropdown selection. This refreshes party
  // data from the backend instead of persisting a stale copy in localStorage.
  useEffect(() => {
    if (!currentUser?.id) {
      restoredPartyUserIdRef.current = null
      return
    }
    if (
      !partyPresets.presetsLoaded
      || partyPresets.rememberedSelectionUserId !== currentUser.id
      || restoredPartyUserIdRef.current === currentUser.id
    ) return

    if (partyPresets.selectedSupplierPresetId) {
      partyPresets.applySupplierPreset(partyPresets.selectedSupplierPresetId, setForm, logo)
    }
    if (partyPresets.selectedBuyerPresetId) {
      partyPresets.applyBuyerPreset(partyPresets.selectedBuyerPresetId, setForm)
    }
    restoredPartyUserIdRef.current = currentUser.id
    // Restore once per authenticated user/session. A later manual selection
    // already applies itself directly and must not trigger a second re-apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    partyPresets.presetsLoaded,
    partyPresets.rememberedSelectionUserId,
    currentUser?.id,
  ])

  const handleCountryChange = (newCountry: CountryCode) => {
    const nextLocale = LOCALES[newCountry].locale
    setCountry(newCountry)
    setActiveLocale(nextLocale)
    setForm(f => ({ ...f, country: newCountry }))
    vat.resetDefaultsForCountry(newCountry)
    localStorage.setItem(LS_COUNTRY, newCountry)
    localStorage.setItem(LS_LOCALE, nextLocale)
  }

  const handleLocaleChange = (locale: string) => {
    if (!countrySupportsLocale(country, locale)) return
    setActiveLocale(locale)
    localStorage.setItem(LS_LOCALE, locale)
  }

  const set = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    // Handmatig andere leveranciersidentiteit: de gekozen relatie (en haar
    // logo) hoort niet meer bij de zichtbare leverancier.
    if (key === 'supplier_name' || key === 'supplier_gln') {
      partyPresets.resetSupplierSelection()
      logo.invalidateParty()
    }
    setForm(f => ({ ...f, [key]: e.target.value }))
  }

  const setDocumentNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    markDocumentNumberManual()
    set('document_number')(e)
  }

  const applySupplierPreset = (id: string) => partyPresets.applySupplierPreset(id, setForm, logo)
  const applyBuyerPreset = (id: string) => partyPresets.applyBuyerPreset(id, setForm)

  const loadTemplate = (id: number) => {
    const tpl = templatesState.templates.find(tp => tp.id === id)
    if (!tpl) return
    // REQ-TESTDATA-007: loading a template while a Financial Scenario is
    // locked would otherwise silently overwrite the visible materialized
    // rows without clearing the lock/preview — clear the scenario first, so
    // the template's lines become ordinary editable lines exactly like any
    // other "switch to Normal" transition.
    if (financialScenarioActive) clearFinancialScenario()
    setDocType(tpl.document_type)
    partyPresets.resetSelection()
    logo.resetForSupplierChange()
    setForm(f => applyTemplateToForm(tpl, f))
    // REQ-TESTDATA-006: a template saved during a Financial Scenario stores
    // materialized rows; loaded into the (ordinary) form they are dematerialized
    // like any Scenario -> Normal transition, so no scenario-only amount is sent.
    if (tpl.default_lines?.length) setLines(tpl.default_lines.map(dematerializeFinancialScenarioLine))
  }

  const deleteTemplate = async (id: number) => {
    try {
      await templatesState.deleteTemplate(id)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Verwijderen mislukt')
    }
  }

  const generateRandomLines = () => priceLists.generateRandomLines({
    lines, roundUnitPrices, roundUnitPriceValue, vatOverrides: vat.vatOverrides, setLines,
    lineGeneration: linesState.lineGeneration,
    onMaterialized: () => linesState.setPrecisionMaterialized(true),
  })

  const validateForm = (): boolean => {
    // REQ-TESTDATA-007 — never let Generate proceed while a scenario is
    // still resolving, failed to resolve, or is selected-but-not-yet-locked
    // (visible lines could still be the pre-scenario ordinary ones at that
    // instant) — "what you see is what you generate" must hold, not "what
    // you will eventually see."
    if (financialScenarioActive) {
      if (scenarioPreview.previewLoading) { setError(t('test_data_generator.financial_scenario.preview_loading')); return false }
      if (scenarioPreview.previewError) { setError(scenarioPreview.previewError); return false }
      if (!scenarioPreview.scenarioLocked) { setError(t('test_data_generator.financial_scenario.preview_not_ready')); return false }
    }
    // REQ-TESTDATA-004 §8 — a stale precision preview (settings changed, or
    // never generated, since Precision test was switched on) MUST NOT be
    // silently submitted as though it matched the current config. Does not
    // apply while a Financial Scenario is active: precision there is
    // resolved as part of the scenario preview above, not via the separate
    // catalogue-mode `precisionMaterialized` flag.
    if (!financialScenarioActive && isPrecisionStale) { setError(t('linesEditor.precision_stale_error')); return false }
    if (!form.document_number.trim()) { setError(t('linesEditor.error_document_number_required')); return false }
    if (!form.supplier_name.trim() || !form.supplier_gln.trim()) { setError(t('linesEditor.error_supplier_required')); return false }
    if (logo.logoPending) { setError(t('test_data_generator.logo_pending_error')); return false }
    if (!form.buyer_name.trim() || !form.buyer_gln.trim()) { setError(t('linesEditor.error_buyer_required')); return false }
    const invalidLines = lines.filter(l => !l.article_number.trim())
    if (invalidLines.length) {
      const ok = window.confirm(t('linesEditor.confirm_missing_article_number'))
      if (!ok) return false
    }
    return true
  }

  const buildRequestBody = () => ({
    document_type: docType,
    xml_format: xmlFormat,
    xml_profile: (xmlFormat === 'ubl21' || xmlFormat === 'cii') && xmlProfile !== 'none' ? xmlProfile : undefined,
    facturx_profile: xmlFormat === 'facturx' ? facturxProfile : undefined,
    document_number_mode: documentNumberMode,
    ...form,
    currency: localeConfig.currency,
    locale: activeLocale,
    logo_base64: logo.payloadLogoBase64,
    compact_pdf_lines: linesState.compactPdfLines,
    include_locale_recognition_text: linesState.includeLocaleRecognitionText && localeSupportsRecognitionText(activeLocale),
    include_ground_truth: linesState.includeGroundTruth && docType === 'invoice' && xmlFormat !== 'facturx',
    // REQ-TESTDATA-003 (v0.8): only sent when the user actually opted into
    // precision-test mode — 'catalogue' mode omits the field entirely so
    // older-shaped request bodies keep working identically server-side.
    line_generation: linesState.lineGeneration.mode === 'precision_test' ? linesState.lineGeneration : undefined,
    // REQ-TESTDATA-004 (v0.8.1): tells the backend `lines` already IS the
    // materialized (possibly hand-edited) canonical precision-test preview,
    // so it must not be transformed a second time. `validateForm()` refuses
    // to submit while the preview is stale, so this is only ever true here
    // for an up-to-date preview. REQ-TESTDATA-007: also true whenever a
    // Financial Scenario is active — the visible `lines` are ALREADY the
    // scenario's canonical (and, if applicable, precision-transformed)
    // materialization from the preview endpoint; without this, `/generate`
    // would run the generic `build_precision_lines()` catalogue transform
    // over the already-materialized scenario rows before
    // `apply_financial_scenario` even sees them — corrupting them, not just
    // the `-TRANSPORT` suffix case this requirement's idempotency contract
    // otherwise guards against.
    precision_materialized: linesState.lineGeneration.mode === 'precision_test'
      ? (financialScenarioActive || linesState.precisionMaterialized)
      : undefined,
    // REQ-TESTDATA-005 (CAP-003 v1.0) — omitted entirely (not sent as `null`)
    // unless the user actually picked a scenario, and only ever sent when the
    // combination is supported — matching the precision-test field's
    // "absent = unchanged" contract above.
    financial_scenario_id: financialScenarioActive ? financialScenarioId : undefined,
    // REQ-TESTDATA-007 review fix — the canonical materialization `M` is
    // not just `lines`: these document-level totals (returned by the same
    // preview call that materialized `lines`) are NOT derivable from lines
    // alone and MUST be submitted verbatim, or apply_financial_scenario's
    // idempotency short-circuit would silently treat them as zero/absent,
    // diverging FIN-DOC-DISCOUNT-001/FIN-PAYMENT-001/FIN-COMBINED-001's
    // generated totals from what the preview panel showed.
    document_allowance_amount: financialScenarioActive ? scenarioPreview.documentAllowanceAmount ?? undefined : undefined,
    document_charge_amount: financialScenarioActive ? scenarioPreview.documentChargeAmount ?? undefined : undefined,
    prepaid_amount: financialScenarioActive ? scenarioPreview.prepaidAmount ?? undefined : undefined,
    lines: lines.map(l => ({
      ...l,
      quantity: Number(l.quantity),
      unit_price: Number(l.unit_price),
      vat_percentage: Number(l.vat_percentage),
    })),
    save_as_template: saveAsTemplate,
    template_name: saveAsTemplate ? templateName : undefined,
  })

  const openEmailModal = () => {
    setError(null)
    setSuccessMsg(null)
    if (!validateForm()) return
    // REQ-TESTDATA-011: snapshot exactly once, after validation. Later
    // generator-state changes (e.g. an async scenario re-resolve) can no
    // longer alter what the open modal shows or sends.
    setEmailSnapshot(freezeSnapshot(buildRequestBody()))
    setShowEmailModal(true)
  }

  const closeEmailModal = () => {
    setShowEmailModal(false)
    setEmailSnapshot(null)
  }

  const loadFromHistory = (params: Record<string, unknown>) => {
    // REQ-TESTDATA-009: history/config-preset payloads replace party fields
    // independently of PartyPreset identity, so any remembered dropdown
    // provenance must be cleared before applying that payload. The logo is
    // never part of such a payload, so any previous logo is dropped too.
    partyPresets.resetSelection()
    logo.resetForSupplierChange()
    const dt = (params.document_type as DocType) ?? 'invoice'
    setDocType(dt)
    const fmtStr = params.xml_format as string
    const fmt: XmlFormat = ['ubl21', 'cii', 'facturx'].includes(fmtStr) ? (fmtStr as XmlFormat) : 'sales005'
    setXmlFormat(fmt)
    localStorage.setItem(LS_XML_FORMAT, fmt)

    const profileStr = params.xml_profile as string | null | undefined
    setXmlProfile(profileStr === 'cius_fr' || profileStr === 'extended_ctc_fr' ? profileStr : 'none')

    const facturxProfileStr = params.facturx_profile as string | null | undefined
    const validFacturxProfiles: FacturxProfile[] = ['minimum', 'basic_wl', 'basic', 'en16931', 'extended']
    setFacturxProfile(
      validFacturxProfiles.includes(facturxProfileStr as FacturxProfile)
        ? (facturxProfileStr as FacturxProfile)
        : 'en16931'
    )

    const requestedLocale = (params.locale as string) ?? 'nl-NL'
    const foundCountry = countryForLocale(requestedLocale) ?? 'NL'
    const resolvedLocale = countrySupportsLocale(foundCountry, requestedLocale)
      ? requestedLocale
      : LOCALES[foundCountry].locale
    setCountry(foundCountry)
    setActiveLocale(resolvedLocale)
    localStorage.setItem(LS_COUNTRY, foundCountry)
    localStorage.setItem(LS_LOCALE, resolvedLocale)

    const str = (key: string) => (params[key] as string | null | undefined) ?? ''
    setForm({
      country: foundCountry,
      document_number: str('document_number'),
      document_date: str('document_date') || new Date().toISOString().slice(0, 10),
      project_number: str('project_number'),
      buyers_order_number: str('buyers_order_number'),
      suppliers_order_number: str('suppliers_order_number'),
      order_response_number: str('order_response_number'),
      desadv_number: str('desadv_number'),
      end_customer_order_number: str('end_customer_order_number'),
      supplier_name: str('supplier_name'),
      supplier_gln: str('supplier_gln'),
      supplier_iban: str('supplier_iban'),
      supplier_vat: str('supplier_vat'),
      supplier_kvk: str('supplier_kvk'),
      supplier_siren: str('supplier_siren'),
      supplier_attn: str('supplier_attn'),
      supplier_address: str('supplier_address'),
      supplier_postal: str('supplier_postal'),
      supplier_city: str('supplier_city'),
      supplier_phone: str('supplier_phone'),
      supplier_email: str('supplier_email'),
      supplier_website: str('supplier_website'),
      buyer_name: str('buyer_name'),
      buyer_gln: str('buyer_gln'),
      buyer_vat: str('buyer_vat'),
      buyer_attn: str('buyer_attn'),
      buyer_address: str('buyer_address'),
      buyer_postal: str('buyer_postal'),
      buyer_city: str('buyer_city'),
      invoicer_name: str('invoicer_name'),
      invoicer_gln: str('invoicer_gln'),
      invoicee_name: str('invoicee_name'),
      invoicee_gln: str('invoicee_gln'),
      delivery_party_name: str('delivery_party_name'),
      delivery_party_gln: str('delivery_party_gln'),
      ship_from_name: str('ship_from_name'),
      ship_from_gln: str('ship_from_gln'),
    })
    if (Array.isArray(params.lines) && params.lines.length > 0) {
      // REQ-TESTDATA-006: reused rows are ordinary rows (see loadTemplate).
      setLines((params.lines as TdLine[]).map(dematerializeFinancialScenarioLine))
    }
  }

  const injection = useErrorInjection({
    buildRequestBody,
    validateForm,
    form,
    setForm,
    refreshDocCounter,
    ensureCurrentAutoDocumentNumber,
  })

  const generate = async () => {
    setError(null)
    setSuccessMsg(null)
    if (!validateForm()) return

    setLoading(true)
    try {
      const freshNumber = await ensureCurrentAutoDocumentNumber(form.document_number)
      const body = { ...buildRequestBody(), document_number: freshNumber }
      const res = await fetch('/api/testdata/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader() },
        body: JSON.stringify(body),
      })
      handleUnauthorized(res)
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: res.statusText }))
        throw new Error(err.detail ?? res.statusText)
      }

      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const safeNumber = freshNumber.replace(/[/\\]/g, '-')
      a.href = url
      // REQ-TESTDATA-008: de backend bepaalt de naam (incl. " - N lines"); een blob-download
      // met `a.download` negeert Content-Disposition, dus lees die hier zelf uit.
      const serverName = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1]
      a.download = serverName ?? (xmlFormat === 'facturx'
        ? `${safeNumber}_${docType}_facturx.pdf`
        : `${safeNumber}_${docType}.zip`)
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setSuccessMsg(
        xmlFormat === 'facturx'
          ? t('test_data_generator.generated_facturx', { doc: docLabels[docType] })
          : body.include_ground_truth
          ? t('test_data_generator.generated_zip_with_ground_truth', { doc: docLabels[docType] })
          : t('test_data_generator.generated_zip', { doc: docLabels[docType] })
      )
      refreshDocCounter()
      if (saveAsTemplate && templateName) {
        await templatesState.loadTemplates()
        setSaveAsTemplate(false)
        setTemplateName('')
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Genereren mislukt')
    } finally {
      setLoading(false)
    }
  }

  return {
    country, handleCountryChange,
    activeLocale, localeOptions, handleLocaleChange,
    localeConfig, docLabels, uiDocLabels, vatCodeOptions,
    docType, setDocType,
    xmlFormat, setXmlFormat: handleXmlFormatChange,
    xmlProfile, setXmlProfile,
    facturxProfile, setFacturxProfile,
    form, setForm,
    lines, setLines,
    templates: templatesState.templates,
    saveAsTemplate, setSaveAsTemplate,
    templateName, setTemplateName,
    loading,
    error, setError,
    successMsg, setSuccessMsg,
    deletingId: templatesState.deletingId,
    logoBase64: logo.logoBase64, logoFileName: logo.logoFileName, logoInputRef: logo.logoInputRef,
    hasSavedLogo: logo.hasSavedLogo, logoIsFromSaved: logo.logoIsFromSaved,
    logoIsFromParty: logo.logoIsFromParty, logoPending: logo.logoPending,
    saveLogoDefault: logo.saveLogoDefault, logoSaving: logo.logoSaving,
    partyPresets: partyPresets.partyPresets,
    selectedSupplierPresetId: partyPresets.selectedSupplierPresetId,
    selectedBuyerPresetId: partyPresets.selectedBuyerPresetId,
    availablePriceLists: priceLists.availablePriceLists,
    randomPriceListId: priceLists.randomPriceListId, setRandomPriceListId,
    randomCount: priceLists.randomCount, setRandomCount: priceLists.setRandomCount,
    randomLoading: priceLists.randomLoading, randomError: priceLists.randomError,
    roundUnitPrices: linesState.roundUnitPrices, setRoundUnitPrices: linesState.setRoundUnitPrices,
    roundUnitPriceValue: linesState.roundUnitPriceValue, setRoundUnitPriceValue: linesState.setRoundUnitPriceValue,
    compactPdfLines: linesState.compactPdfLines, setCompactPdfLines: linesState.setCompactPdfLines,
    includeLocaleRecognitionText: linesState.includeLocaleRecognitionText,
    setIncludeLocaleRecognitionText: linesState.setIncludeLocaleRecognitionText,
    includeGroundTruth: linesState.includeGroundTruth,
    setIncludeGroundTruth: linesState.setIncludeGroundTruth,
    lineGeneration: linesState.lineGeneration,
    setLineGeneration: linesState.setLineGeneration,
    // REQ-TESTDATA-004 (v0.8.1)
    precisionMaterialized: linesState.precisionMaterialized,
    isPrecisionStale,
    updateLineDecimal: linesState.updateLineDecimal,
    vatOverrides: vat.vatOverrides, setVatOverrides: vat.setVatOverrides,
    showVatAdd: vat.showVatAdd, setShowVatAdd: vat.setShowVatAdd,
    newVatCode: vat.newVatCode, setNewVatCode: vat.setNewVatCode,
    newVatPct: vat.newVatPct, setNewVatPct: vat.setNewVatPct,
    showEmailModal, setShowEmailModal,
    emailSnapshot, closeEmailModal,
    isInvoice: docType === 'invoice',
    isCreditNote: docType === 'credit_note',
    isDA: docType === 'despatch_advice',
    set,
    setDocumentNumber,
    applySupplierPreset, applyBuyerPreset,
    updateLine: linesState.updateLine, addLine: linesState.addLine, removeLine: linesState.removeLine,
    handleLogoUpload: logo.handleLogoUpload, clearLogo: logo.clearLogo, selectSavedLogo: logo.selectSavedLogo,
    toggleSaveLogoDefault: logo.toggleSaveLogoDefault, removeSavedLogo: logo.removeSavedLogo,
    loadTemplate, deleteTemplate,
    addVatOverride: vat.addVatOverride, removeVatOverride: vat.removeVatOverride,
    generateRandomLines,
    buildRequestBody,
    validateForm,
    openEmailModal,
    generate,
    injection,
    loadFromHistory,
    refreshDocCounter,
    buildDocNumber: (type: DocType, initials: string, counter: number) => buildDocNumber(type, initials, counter),
    docNumCounterRef,
    documentNumberMode,
    markDocumentNumberManual,
    ensureCurrentAutoDocumentNumber,
    currentUserInitials: currentUser?.initials ?? '',
    // REQ-TESTDATA-005 (CAP-003 v1.0)
    financialScenarioId, setFinancialScenarioId: selectFinancialScenario,
    financialScenarioSupported, financialScenarioUnsupportedForCountry,
    // REQ-TESTDATA-007 (CAP-003 v1.2)
    financialScenarioActive,
    financialScenarioPreview: scenarioPreview.preview,
    financialScenarioLocked: scenarioPreview.scenarioLocked,
    financialScenarioPreviewLoading: scenarioPreview.previewLoading,
    financialScenarioPreviewError: scenarioPreview.previewError,
  }
}
